import { LogOut, MicOff, Settings, Video, VideoOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { avatarSrc } from '../../shared/avatars'
import type { ClientIdentity } from '../../shared/types'

interface JoinGateProps {
  identity: ClientIdentity
  cameraState: 'idle' | 'starting' | 'ready' | 'denied' | 'error'
  stream: MediaStream | null
  cameraOn: boolean
  onStartCamera: () => Promise<MediaStream | null>
  onSetCamera: (enabled: boolean) => void
  onOpenSettings: () => void
  onJoin: () => void
  onLogout: () => void
  previewMode?: boolean
}

interface PeekUser {
  id: string
  name: string
  role: string
  color: string
  avatarUrl?: string
  cameraOn: boolean
}

function CameraPreview({ stream, identity }: { stream: MediaStream | null; identity: ClientIdentity }) {
  const video = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    if (!video.current) return
    video.current.srcObject = stream
    void video.current.play().catch(() => undefined)
  }, [stream])
  return stream
    ? <video ref={video} autoPlay muted playsInline />
    : <span className="preflight-avatar" style={{ '--avatar': identity.color } as React.CSSProperties}><img src={avatarSrc(identity)} alt="" /></span>
}

export function JoinGate({ identity, cameraState, stream, cameraOn, onStartCamera, onSetCamera, onOpenSettings, onJoin, onLogout, previewMode = false }: JoinGateProps) {
  const [peek, setPeek] = useState(false)
  const [peekUsers, setPeekUsers] = useState<PeekUser[]>([])
  useEffect(() => { void onStartCamera() }, [onStartCamera])
  useEffect(() => {
    if (!peek) return
    let active = true
    const load = async () => {
      const response = await fetch(`/api/room/peek${previewMode ? '?preview=1' : ''}`, { credentials: 'same-origin' })
      if (!response.ok || !active) return
      const body = await response.json() as { users?: PeekUser[] }
      if (active) setPeekUsers(body.users ?? [])
    }
    void load()
    const interval = window.setInterval(() => void load(), 10_000)
    return () => { active = false; window.clearInterval(interval) }
  }, [peek, previewMode])

  return (
    <main className="join-gate">
      <header className="join-header">
        <span className="wordmark">FOCUS</span>
        <button onClick={onLogout}><LogOut size={16} /> Sign out</button>
      </header>
      <div className={`preflight-layout ${peek ? 'peeking' : ''}`}>
        <section className="preflight-card">
          <header><strong>Are you ready to join?</strong><span><button onClick={() => setPeek(!peek)}>Take a peek</button><button className="preflight-join" onClick={onJoin}>Join</button></span></header>
          <div className="preflight-video"><CameraPreview stream={cameraOn ? stream : null} identity={identity} /><small>{identity.name}</small></div>
          <footer>
            <button onClick={() => onSetCamera(!cameraOn)}>{cameraOn ? <Video /> : <VideoOff />}<span>{cameraOn ? 'Turn off' : 'Turn on'}</span></button>
            <button onClick={onOpenSettings}><Settings /><span>Settings</span></button>
            <button disabled><MicOff /><span>Mic off</span></button>
          </footer>
          {cameraState === 'denied' && <p className="join-warning">Camera blocked</p>}
        </section>
        {peek && <aside className="room-peek"><strong>Currently in this session</strong>{peekUsers.length ? <div>{peekUsers.slice(0, 16).map((user) => <span key={user.id} style={{ '--peek': user.color } as React.CSSProperties}><img src={avatarSrc(user)} alt="" /><small>{user.name}</small></span>)}</div> : <p>No one here yet</p>}</aside>}
      </div>
    </main>
  )
}
